import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbWaveSquare } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type LiveSessionLoadPayload, parseLiveSessionLoadPayload } from "./liveSessionLoadSchema";

interface LiveSessionLoadRendererProps {
  block: StreamingBlock;
}

// The session URL arrives from tool output and is untrusted; only https URLs are
// safe to put in an href or to hand to the host's external-URL opener.
const safeSessionUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

export const LiveSessionLoadRenderer = (props: LiveSessionLoadRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LiveSessionLoadPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLiveSessionLoadPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  // The session URL is absolute and points outside the chat, so a surface that
  // routes its own navigation (chief → App.OpenURL) takes it over from the
  // anchor. Modified clicks fall through so cmd-click still opens a new tab.
  const openSession = (e: MouseEvent, url: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (!rt.actions.openExternalUrl) return;
    e.preventDefault();
    rt.actions.openExternalUrl(url);
  };

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("live_session_load_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // Nothing loaded and nothing to report renders no card at all.
          <Show when={data().sessions.length > 0 || data().notFound.length > 0 || data().userError}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbWaveSquare class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("live_session_load_card.title")}</span>
                <Show when={data().sessions.length > 0}>
                  <span class="ml-auto shrink-0 text-xs text-muted-text">
                    {data().sessions.length === 1
                      ? rt.t("live_session_load_card.results_one")
                      : rt.t("live_session_load_card.results", { count: String(data().sessions.length) })}
                  </span>
                </Show>
              </div>

              <Show when={data().userError}>
                <div class="p-3 text-sm text-muted-text">{data().userError}</div>
              </Show>

              <ul class="divide-y divide-primary-border">
                <For each={data().sessions}>
                  {(session) => (
                    <li>
                      <Show
                        when={safeSessionUrl(session.url)}
                        fallback={
                          <div class="flex items-center gap-2 px-3 py-2">
                            <span class="min-w-0 truncate text-sm font-medium text-primary-text">{session.title}</span>
                          </div>
                        }
                      >
                        {(url) => (
                          <a
                            href={url()}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => openSession(e, url())}
                            class="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-panel-hover"
                          >
                            <span class="min-w-0 truncate text-sm font-medium text-primary-text">{session.title}</span>
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().notFound.length > 0}>
                <div class="border-t border-primary-border px-3 py-2">
                  <p class="text-xs font-medium tracking-wide text-muted-text uppercase">
                    {rt.t("live_session_load_card.not_found")}
                  </p>
                  <ul class="mt-1 space-y-0.5">
                    <For each={data().notFound}>
                      {(nf) => <li class="text-sm wrap-break-word text-muted-text">{nf.reason}</li>}
                    </For>
                  </ul>
                </div>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
